import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../../../utils/api';
import { money } from '../../../utils/region';
import CompliancePanel from '../../Bookkeeping/CompliancePanel';
import type { AwAging, AwAr, AwCashFlowSummary, AwDeadline, AwMonthly, AwOverviewExtras } from '../../../types';
import { AgingSection } from './Reports';
import type { AwCtx } from '../ctx';
import { amt, baht, btnPrimary, card, dmy, errBox, note, num, openBlob, pillAmber, pillGreen, pillGrey, sectionHead, table, td, th } from '../ui';
import { Stat as RegisterStat } from '../dialog';

// ── Dashboard panes (owner 2026-10-13) ───────────────────────────────────────────────────────────
// Rule: nothing here is computed on the client. The overview shows the statements' own totals and,
// under them, the same P&L rule bucketed by month on the server (/acct-workspace/monthly); AR is the
// server's per-currency re-mount of the owner console; the cash-flow cards are the fields of the
// statement the PDF prints (/acct-workspace/cash-flow-summary); deadlines are the compliance rows
// as stored. Still no YoY and no forecast — the owner console ruled those out for lack of data and
// that ruling stands. Batch 4 adds the "Registers" row under the overview: counts and the registers'
// own cached figures from /acct-workspace/overview-extras (open payables per currency, pending
// reimbursements, unmatched bank lines), each card a link into its submenu — again nothing derived.

const Stat = ({ label, value, tone, sub, subTone }: { label: string; value: React.ReactNode; tone?: string; sub?: string; subTone?: string }) => (
  <div style={{ ...card, minWidth: 170, flex: '1 1 170px' }}>
    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 800, color: tone || 'var(--text-primary)', marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {sub && <div style={{ fontSize: 11.5, fontWeight: 700, color: subTone || 'var(--text-tertiary)', marginTop: 2 }}>{sub}</div>}
  </div>
);

const ymLabel = (ym: string) => { const [y, m] = String(ym || '').split('-'); return y && m ? `${m}/${y}` : ym; };
// Two-tone bars: income in the accent, expense in amber — the same pair every row, so the eye reads
// the gap between them, not the colour.
const INCOME_BAR = 'var(--accent)';
const EXPENSE_BAR = '#F59E0B';

/** Month-by-month income / expense / result of the period — the P&L's own definition per month,
 *  served already bucketed. Both bars share ONE scale (the larger of income or expense across every
 *  month) so a month's two bars compare with each other and with the other months. Visual only. */
function MonthlyTrend({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [data, setData] = useState<AwMonthly | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    setErr('');
    api.awMonthly(ctx.clientId, ctx.periodId).then((d: AwMonthly) => { if (alive) setData(d); }).catch((e: any) => { if (alive) { setData(null); setErr(String(e?.message || 'error')); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, ctx.reload]);
  const months = data?.months || [];
  const max = months.reduce((m, r) => Math.max(m, Math.abs(Number(r.income) || 0), Math.abs(Number(r.expense) || 0)), 0);
  const pct = (v: number) => max ? Math.round(Math.abs(v) / max * 100) : 0;
  const empty = !months.length || months.every(r => !Number(r.income) && !Number(r.expense));
  return (
    <div>
      <div style={sectionHead}>{t('aw_trend_title')}</div>
      <div style={card}>
        {err ? <div style={errBox}>{err}</div> : !data ? <i className="ti ti-loader" style={{ color: 'var(--text-tertiary)' }} /> : empty ? (
          <div style={note}>{t('aw_trend_no_entries')}</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={table}>
                <thead>
                  <tr>
                    <th style={th}>{t('aw_trend_month')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('aw_trend_income')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('aw_trend_expense')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('aw_trend_profit')}</th>
                    <th style={{ ...th, width: '34%' }} aria-hidden />
                  </tr>
                </thead>
                <tbody>
                  {months.map(r => {
                    const inc = Number(r.income) || 0, exp = Number(r.expense) || 0, pr = Number(r.profit) || 0;
                    const zero = !inc && !exp;
                    const tone = zero ? 'var(--text-tertiary)' : 'var(--text-primary)';
                    return (
                      <tr key={r.ym}>
                        <td style={{ ...td, color: tone, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{ymLabel(r.ym)}</td>
                        <td style={{ ...num, color: tone }}>{baht(inc)}</td>
                        <td style={{ ...num, color: tone }}>{baht(exp)}</td>
                        <td style={{ ...num, color: pr < 0 ? '#B42318' : tone, fontWeight: zero ? 400 : 700 }}>{baht(pr)}</td>
                        <td style={{ ...td, minWidth: 140 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <div style={{ height: 5, borderRadius: 3, background: INCOME_BAR, width: `${pct(inc)}%` }} title={t('aw_trend_income')} />
                            <div style={{ height: 5, borderRadius: 3, background: EXPENSE_BAR, width: `${pct(exp)}%` }} title={t('aw_trend_expense')} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ ...note, marginTop: 8, display: 'flex', gap: 14, alignItems: 'center' }}>
              <span><i style={{ display: 'inline-block', width: 10, height: 5, borderRadius: 3, background: INCOME_BAR, marginRight: 5, verticalAlign: 'middle' }} />{t('aw_trend_income')}</span>
              <span><i style={{ display: 'inline-block', width: 10, height: 5, borderRadius: 3, background: EXPENSE_BAR, marginRight: 5, verticalAlign: 'middle' }} />{t('aw_trend_expense')}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** The Registers row of the overview: the three registers batches 2–4 built, each as a stat card that is
 *  a <Link> to its submenu (the same URL with menu/sub swapped — the Expenses pane's idiom). The AP cards
 *  are one per currency, never summed (the owner console's rule). A company with no portal account has
 *  no registers: /summary already says so (hasMerchant), and /overview-extras answers 200 with
 *  hasMerchant:false and zeros rather than 404 — either way the row is simply absent, never three zero
 *  cards each leading into the "no portal account" card (owner: no grey menus; reviewer 2026-10-13).
 *  The 404 branch stays as a fallback for an older server. */
function RegistersRow({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const loc = useLocation();
  const [params] = useSearchParams();
  const [d, setD] = useState<AwOverviewExtras | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'hidden' | 'error'>('loading');
  const [err, setErr] = useState('');
  const noMerchant = !!ctx.summary && !ctx.summary.hasMerchant;
  useEffect(() => {
    let alive = true;
    setErr('');
    if (noMerchant) { setState('hidden'); return; }   // known before any call: nothing to fetch, nothing to show
    setState('loading');
    api.awOverviewExtras(ctx.clientId, ctx.periodId)
      .then((x: AwOverviewExtras) => { if (!alive) return; if (x?.hasMerchant === false) setState('hidden'); else { setD(x); setState('ok'); } })
      .catch((e: any) => { if (!alive) return; if (e?.status === 404) setState('hidden'); else { setState('error'); setErr(String(e?.message || 'error')); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, ctx.reload, noMerchant]);
  const to = (menu: string, sub: string) => { const n = new URLSearchParams(params); n.set('menu', menu); n.set('sub', sub); return { pathname: loc.pathname, search: `?${n.toString()}` }; };
  // What is still owed, in the register that now holds it (batch 7 R5) — see payablesTo below.
  const unpaidTo = () => { const n = new URLSearchParams(params); n.set('menu', 'expenses'); n.set('sub', 'expense_records'); n.set('pay', 'unpaid'); return { pathname: loc.pathname, search: `?${n.toString()}` }; };
  if (state === 'hidden') return null;
  const linkStyle: React.CSSProperties = { flex: '1 1 170px', minWidth: 170, display: 'flex', textDecoration: 'none', color: 'inherit' };
  const ap = d?.ap?.byCurrency || [];
  return (
    <div>
      <div style={sectionHead}>{t('aw_rb_reg_title')}</div>
      {state === 'error' ? <div style={errBox}>{err}</div> : state === 'loading' || !d ? <div style={card}><i className="ti ti-loader" style={{ color: 'var(--text-tertiary)' }} /></div> : (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {/* ⛔ One card per currency, no total across them. An empty register is one card saying so, linking to where bills are entered. */}
            {!ap.length ? (
              <Link to={unpaidTo()} style={linkStyle}><RegisterStat label={t('aw_rb_reg_ap')} value={amt(0)} sub={t('aw_rb_reg_ap_none')} /></Link>
            ) : ap.map(r => (
              <Link key={r.currency} to={unpaidTo()} style={linkStyle}>
                <RegisterStat label={`${t('aw_rb_reg_ap')} · ${r.currency}`} value={amt(r.outstanding)}
                  sub={`${t('aw_rb_reg_ap_sub', { n: r.open_count })}${Number(r.overdue) ? ` · ${t('aw_rb_reg_ap_overdue')}: ${amt(r.overdue)}` : ''}`}
                  tone={Number(r.overdue) ? '#92400E' : undefined} />
              </Link>
            ))}
            {/* Reimbursements live under Payroll since batch 10 R3 (menus.ts; the old address is an alias). */}
            <Link to={to('payroll', 'reimbursements')} style={linkStyle}>
              <RegisterStat label={t('aw_rb_reg_claims')} value={d.reimbursements.submitted_count} sub={t('aw_rb_reg_claims_sub', { n: d.reimbursements.approved_unpaid_count })}
                tone={Number(d.reimbursements.submitted_count) ? '#92400E' : undefined} />
            </Link>
            <Link to={to('banking', 'transactions')} style={linkStyle}>
              <RegisterStat label={t('aw_rb_reg_bank')} value={d.bank.unmatched_lines}
                sub={[t('aw_rb_reg_bank_sub', { n: d.bank.accounts }), d.bank.latest_statement_to ? t('aw_rb_reg_bank_latest', { date: dmy(d.bank.latest_statement_to) }) : ''].filter(Boolean).join(' · ')}
                tone={Number(d.bank.unmatched_lines) ? '#92400E' : undefined} />
            </Link>
          </div>
          <div style={{ ...note, marginTop: 6 }}>{t('aw_rb_reg_note')}</div>
        </>
      )}
    </div>
  );
}

export function OverviewPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [fs, setFs] = useState<any>(null);
  const [drafts, setDrafts] = useState<number | null>(null);
  // How many of those drafts are built from a document that has moved since (phase 5). The count
  // route has always answered it; nothing read the answer, so the one screen that shows the draft
  // figure could say "7 drafts" while every one of them was unpostable.
  const [stale, setStale] = useState(0);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.ledgerStatements(ctx.periodId).then((d: any) => { if (alive) setFs(d); }).catch((e: any) => { if (alive) { setFs(null); setErr(e.message); } });
    api.jnlDraftCount(ctx.clientId, ctx.periodId)
      .then((d: any) => { if (alive) { setDrafts(Number(d?.count) || 0); setStale(Number(d?.stale) || 0); } })
      .catch(() => { if (alive) { setDrafts(null); setStale(0); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, ctx.reload]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Stat label={t('aw_ov_income')} value={fs ? baht(fs.pl?.totals?.income) : '…'} />
        {/* 成本 and 毛利 beside the income the client already had (the firm's client, 2026-10-16,
            question 1). Both come from the server's working account — the ONE place that knows
            which accounts are cost of sales (chart_of_accounts.fs_section) and that carries
            opening and closing stock — so this card cannot disagree with the บัญชีทำการ the firm
            files. When nothing has been placed yet the gross profit would read as the whole of
            sales, which is a lie of omission, so the card says that instead of showing it. */}
        <Stat label={t('aw_ov_cost')} value={fs ? (fs.wa ? baht(fs.wa.costOfSales) : '—') : '…'} />
        <Stat label={t('aw_ov_gross')} value={fs ? (fs.wa && fs.wa.salesTotal ? baht(fs.wa.grossProfit) : '—') : '…'}
          sub={!fs?.wa ? undefined
            : !fs.wa.salesTotal ? t('aw_ov_gross_none')
              : fs.wa.unplaced ? t('aw_ov_gross_unplaced', { n: fs.wa.unplaced, amount: baht(fs.wa.unplacedAmount) })
                : undefined}
          subTone={fs?.wa?.unplaced ? '#B45309' : undefined} />
        <Stat label={t('aw_ov_expense')} value={fs ? baht(fs.pl?.totals?.expense) : '…'} />
        <Stat label={t('aw_ov_profit')} value={fs ? baht(fs.pl?.totals?.profit) : '…'} tone={fs && Number(fs.pl?.totals?.profit) < 0 ? '#B42318' : undefined} />
        <Stat label={t('aw_ov_assets')} value={fs ? baht(fs.sfp?.totals?.assets) : '…'} />
        <Stat label={t('aw_ov_entries')} value={ctx.period ? ctx.period.entries : '—'} />
        <Stat label={t('aw_ov_drafts')} value={drafts === null ? '—' : drafts} tone={drafts ? '#92400E' : undefined}
          sub={stale > 0 ? t('aw_ov_drafts_stale', { n: stale }) : undefined} subTone={stale > 0 ? '#B42318' : undefined} />
      </div>
      {/* ⛔ What the figures above do NOT include. A statement counts POSTED entries, so an invoice
          imported this morning moves nothing here until somebody generates a draft and posts it —
          and the client's question was where to see revenue and cost "in real time after
          importing". Saying the size of the gap is the honest version of that: the count is
          exactly what pressing Generate would work on (the same two conditions
          services/draftEntries.ts generates on). */}
      {fs?.pending?.lines > 0 && (
        <div style={{ fontSize: 12.5, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A',
                      borderRadius: 8, padding: '7px 11px' }}>
          <i className="ti ti-clock-pause" aria-hidden />{' '}
          {t('aw_ov_pending', { count: fs.pending.lines, n: fs.pending.lines,
            sales: baht(fs.pending.sales), purchases: baht(fs.pending.purchases) })}
        </div>
      )}
      {fs && <div style={note}>{t('aw_ov_basis')}</div>}
      {/* The unbalanced warning exactly as the statutory-books page states it: on screen, not left
          for whoever opens the PDF. The server's other warnings are data and print as they come. */}
      {fs?.sfp && !fs.sfp.balanced && (
        <div style={{ ...errBox, fontWeight: 700 }}>
          <i className="ti ti-alert-triangle" aria-hidden /> งบไม่ดุล — {t('pa_sb_unbalanced_why')}
        </div>
      )}
      {(fs?.sfp?.warnings || []).filter((w: string) => !/ไม่ดุล/.test(w)).map((w: string, i: number) => (
        <div key={i} style={{ fontSize: 12, color: '#92400E' }}><i className="ti ti-info-circle" aria-hidden /> {w}</div>
      ))}
      {fs && <div style={note}>{dmy(fs.from)} – {dmy(fs.to)}</div>}
      <RegistersRow ctx={ctx} />
      <MonthlyTrend ctx={ctx} />
    </div>
  );
}

export function ArApPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const loc = useLocation();
  const [params] = useSearchParams();
  const [ar, setAr] = useState<AwAr | null>(null);
  const [err, setErr] = useState('');
  // The AP side is the payables register (batch 3), read through /aging like the Reports pane: per
  // currency with the server's five buckets, never summed across currencies, nothing re-derived here.
  const [aging, setAging] = useState<AwAging | null>(null);
  const [apErr, setApErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.awAr(ctx.clientId).then((d: AwAr) => { if (alive) setAr(d); }).catch((e: any) => { if (alive) setErr(e.message); });
    api.awAging(ctx.clientId).then((d: AwAging) => { if (alive) setAging(d); }).catch((e: any) => { if (alive) setApErr(String(e?.message || 'error')); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);
  // Same page, Expenses › Expense records filtered to what is still owed: the link rebuilds the
  // current URL with menu/sub swapped (the Expenses pane's idiom).
  // ⛔ The register, not Payments: the Payments submenu went in batch 7 R5 because the register holds
  // the bill and its payment on one row. pay=unpaid is what makes it show the same list the card
  // counts; without it the reader lands on every expense and the number stops matching the screen.
  const payablesTo = useMemo(() => {
    const n = new URLSearchParams(params);
    n.set('menu', 'expenses'); n.set('sub', 'expense_records'); n.set('pay', 'unpaid');
    return { pathname: loc.pathname, search: `?${n.toString()}` };
  }, [params, loc.pathname]);
  const ap = aging?.ap;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={sectionHead}>{t('aw_ar_by_currency')}</div>
        <div style={card}>
          {err ? <div style={errBox}>{err}</div> : !ar ? <i className="ti ti-loader" style={{ color: 'var(--text-tertiary)' }} /> : !ar.byCurrency.length ? (
            <div style={note}>{t('aw_ar_none')}</div>
          ) : (
            <table style={table}>
              <thead><tr><th style={th}>{t('aw_col_currency')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_col_outstanding')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_col_count')}</th></tr></thead>
              <tbody>
                {/* ⛔ One row per currency, no total row: the owner console never adds currencies together. */}
                {ar.byCurrency.map(r => (
                  <tr key={r.currency}><td style={td}>{r.currency}</td><td style={num}>{money(r.outstanding, 2)}</td><td style={num}>{r.count}</td></tr>
                ))}
              </tbody>
            </table>
          )}
          {ar?.oldest && (
            <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-secondary)' }}>
              <b>{t('aw_ar_oldest')}:</b> {ar.oldest.doc_no} · {dmy(ar.oldest.due_date)} · <span style={pillAmber}>{t('aw_ar_days', { n: ar.oldest.days })}</span>
            </div>
          )}
          <div style={{ ...note, marginTop: 8 }}>{t('aw_ar_never_summed')}</div>
        </div>
      </div>
      {/* ── 应收/应付客户余额 (the client's question 8) ────────────────────────────────────────
          The tables above sum per CURRENCY and the registers list per DOCUMENT; neither answers
          "what does this customer owe us". These two do, read live from the documents — nothing
          here waits for a month to be closed. ⛔ Still per currency: one customer with a USD and a
          THB invoice owes two balances, and a single number would be neither. */}
      {(aging?.ar?.byParty?.length || aging?.ap?.byParty?.length) ? (
        <div>
          <div style={sectionHead}>{t('aw_party_balances')}</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {([['ar', aging?.ar?.byParty || [], 'aw_ar_by_party', 'aw_col_customer'],
              ['ap', aging?.ap?.byParty || [], 'aw_ap_by_party', 'aw_pay_vendor']] as const).map(([side, parties, headKey, nameKey]) => (
              <div key={side} style={{ ...card, flex: '1 1 320px', padding: 0, overflowX: 'auto' }}>
                <div style={{ ...sectionHead, padding: '10px 12px 0' }}>{t(headKey)}</div>
                {!parties.length ? <div style={{ ...note, padding: '8px 12px 12px' }}>{t('aw_ar_none')}</div> : (
                  <table style={table}>
                    <thead><tr>
                      <th style={th}>{t(nameKey)}</th>
                      <th style={th}>{t('aw_col_currency')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_col_outstanding')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_col_count')}</th>
                    </tr></thead>
                    <tbody>
                      {parties.slice(0, 100).map(p2 => (
                        <tr key={p2.key}>
                          <td style={td}>
                            {p2.name || '—'}
                            {/* A supplier grouped only by the folded name is NOT one creditor
                                proven: two spellings stay two rows until a tax id joins them. */}
                            {p2.keyed === 'name' && <span title={t('aw_party_by_name_hint')} style={{ color: '#B45309', marginLeft: 5 }}>*</span>}
                          </td>
                          <td style={td}>{p2.currency}</td>
                          <td style={num}>{money(p2.total, 2)}</td>
                          <td style={num}>{p2.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {parties.some(x => x.keyed === 'name') && <div style={{ ...note, padding: '0 12px 10px' }}>{t('aw_party_by_name_hint')}</div>}
                {parties.length > 100 && <div style={{ ...note, padding: '0 12px 10px' }}>{t('aw_party_capped', { n: parties.length })}</div>}
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <div>
        <div style={sectionHead}>{t('aw_ap_by_currency')}</div>
        {apErr ? <div style={errBox}>{apErr}</div> : !aging ? <i className="ti ti-loader" style={{ color: 'var(--text-tertiary)' }} /> : !ap?.available || !ap.byCurrency.length ? (
          // An empty register is one plain line, not a grey card (owner: no grey menus).
          <div style={{ ...note, fontSize: 12.5 }}>{t('aw_aging_ap_none')}</div>
        ) : (
          <AgingSection buckets={aging.buckets} byCurrency={ap.byCurrency} rows={[]} emptyKey="aw_aging_ap_none" nameKey="aw_pay_vendor" issuedKey="aw_pay_bill_date" showRows={false} />
        )}
        <div style={{ marginTop: 8, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Link to={payablesTo} style={{ fontWeight: 700, color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5 }}>
            {t('aw_ap_open_register')} <i className="ti ti-arrow-right" style={{ fontSize: 12 }} aria-hidden />
          </Link>
          <span style={note}>{t('aw_ar_never_summed')}</span>
        </div>
      </div>
    </div>
  );
}

// The two statement warnings this pane already states in its own words — the verdict pill and the
// unclassified line (financialStatements.cashFlow pushes both whenever the condition holds) — so each
// fact reads once, the way OverviewPane drops the /ไม่ดุล/ twin. Their extra detail (the account codes,
// the exact difference) stays where the statement prints it: inline under Reports › Cash flow and in
// the PDF a click away. Any OTHER warning (e.g. "no account is marked as cash") still prints as it comes.
const CF_RESTATED = [/ยังไม่ได้จัดประเภทกระแสเงินสด/, /กระแสเงินสดไม่ลงตัว/];

export function DashCashFlowPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const loc = useLocation();
  const [params] = useSearchParams();
  const [cf, setCf] = useState<AwCashFlowSummary | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [openErr, setOpenErr] = useState('');
  useEffect(() => {
    let alive = true;
    setErr('');
    api.awCashFlowSummary(ctx.clientId, ctx.periodId).then((d: AwCashFlowSummary) => { if (alive) setCf(d); }).catch((e: any) => { if (alive) { setCf(null); setErr(String(e?.message || 'error')); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, ctx.reload]);
  // Same page, other submenu: setNav lives in the shell and is not in ctx, so the link rebuilds the
  // current URL with menu/sub swapped (the Expenses pane does the same) — the unclassified accounts
  // are fixed in the chart, so the line that reports them leads there.
  const chartTo = useMemo(() => {
    const n = new URLSearchParams(params); n.set('menu', 'accounting'); n.set('sub', 'chart');
    return { pathname: loc.pathname, search: `?${n.toString()}` };
  }, [params, loc.pathname]);
  const open = async () => {
    if (busy) return; setBusy(true); setOpenErr('');
    try { openBlob(await api.axDocUrl('cash-flow.pdf', { clientId: ctx.clientId, periodId: ctx.periodId })); }
    catch (e: any) { setOpenErr(e.message); } finally { setBusy(false); }
  };
  const val = (n: any) => cf ? baht(n) : '…';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>งบกระแสเงินสด</div>
          <div style={note}>{t('pa_fs_cf')}{ctx.period ? ` · ${dmy(ctx.period.start_date)} – ${dmy(ctx.period.end_date)}` : ''}</div>
        </div>
        <div style={{ flex: 1 }} />
        {/* The statement's own verdict: net change vs the movement on the cash accounts (financialStatements.cashFlow). */}
        {cf && (
          <span style={cf.reconciles ? pillGreen : pillAmber}>
            <i className={cf.reconciles ? 'ti ti-check' : 'ti ti-alert-triangle'} aria-hidden /> {t(cf.reconciles ? 'aw_cf_reconciles' : 'aw_cf_not_reconciles')}
          </span>
        )}
        <button onClick={open} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>
          <i className={busy ? 'ti ti-loader' : 'ti ti-file-type-pdf'} aria-hidden /> {t('aw_cf_open')}
        </button>
      </div>
      {err && <div style={errBox}>{err}</div>}
      {openErr && <div style={errBox}>{openErr}</div>}
      {/* The cards are the lines the statement itself prints (financialStatements.cashFlowBody): the three
          activity totals, the net change, and the actual movement on the cash accounts it is checked
          against. cashFlow()'s `opening` / `closing` fields are deliberately NOT shown: within a period the
          statement has no opening figure (`opening` is a constant 0 there) and `closing` is the cash
          MOVEMENT, not a balance — labelled "opening / closing cash" they read 0.00 for every company in
          its second year. When the summary could not be fetched the cards are hidden rather than left as
          six loading glyphs beside the error; the PDF button stays usable. */}
      {!err && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <Stat label={t('aw_cf_operating')} value={val(cf?.operatingTotal)} />
          <Stat label={t('aw_cf_investing')} value={val(cf?.investingTotal)} />
          <Stat label={t('aw_cf_financing')} value={val(cf?.financingTotal)} />
          <Stat label={t('aw_cf_net_change')} value={val(cf?.netChange)} tone={cf && Number(cf.netChange) < 0 ? '#B42318' : undefined} />
          <Stat label={t('aw_cf_actual')} value={val(cf?.actualCashMovement)} tone={cf && !cf.reconciles ? '#92400E' : undefined} />
        </div>
      )}
      {!err && <div style={note}>{t('aw_cf_no_balances')}</div>}
      {cf && Number(cf.unclassified) > 0 && (
        <div style={{ fontSize: 12.5, color: '#92400E', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span><i className="ti ti-alert-triangle" aria-hidden /> {t('aw_cf_unclassified', { n: cf.unclassified })}</span>
          <Link to={chartTo} style={{ fontWeight: 700, color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {t('aw_cat_empty_link')} <i className="ti ti-arrow-right" style={{ fontSize: 12 }} aria-hidden />
          </Link>
        </div>
      )}
      {(cf?.warnings || []).filter(w => !CF_RESTATED.some(re => re.test(w))).map((w, i) => (
        <div key={i} style={{ fontSize: 12, color: '#92400E' }}><i className="ti ti-info-circle" aria-hidden /> {w}</div>
      ))}
    </div>
  );
}

const statusPill = (s: string): React.CSSProperties => s === 'filed' ? pillGreen : s === 'na' ? pillGrey : pillAmber;

export function TaxDeadlinesPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<AwDeadline[] | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.awDeadlines(ctx.clientId).then((d: any) => { if (alive) setRows(d.rows || []); }).catch((e: any) => { if (alive) { setRows([]); setErr(e.message); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={card}>
        {err && <div style={{ ...errBox, marginBottom: 8 }}>{err}</div>}
        {rows === null ? <i className="ti ti-loader" style={{ color: 'var(--text-tertiary)' }} /> : !rows.length ? <div style={note}>{t('aw_deadlines_none')}</div> : (
          <table style={table}>
            <thead><tr><th style={th}>{t('aw_col_kind')}</th><th style={th}>{t('aw_col_period')}</th><th style={th}>{t('aw_col_due')}</th><th style={th}>{t('aw_col_status')}</th></tr></thead>
            <tbody>
              {rows.map(r => (
                // r.label is the server's own name for the filing (services/compliance.ts) — data, not t().
                <tr key={r.id}><td style={td}>{r.label || r.kind}</td><td style={td}>{r.period}</td><td style={td}>{dmy(r.due_date)}</td><td style={td}><span style={statusPill(r.status)}>{r.status}</span></td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {ctx.periodId && <CompliancePanel clientId={ctx.clientId} periodId={ctx.periodId} />}
    </div>
  );
}
